import { BusinessKeywordsMapping } from 'api/client/mapping/business-keywords-mapping';
import { BusinessTypesMapping } from 'api/client/mapping/business-types-mapping';
import { AddApproverDelegateModel, AddCountryApproverModel, AddCountryContactModel, AddDelegateApproverModel, CountriesClient, ISystemUserCountryRoleInfoModel, ISystemUserDetailDTO, RemoveApproverDelegateModel, RemoveCountryApproverModel, RemoveCountryContactModel, RemoveDelegateApproverModel, SystemUsersClient, SystemUserUpdateDTO } from 'api/swagger/ApiClient';
import { autoinject, newInstance } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { WidgetButton } from 'controls/as-widget-stack';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { EventHelper } from 'framework/aurelia_tools/event-helper';
import { log } from 'framework/script_utilities/dev-decorators';
import { UserCountryRoleModel } from 'model/forms/user-country-role-model';
import { CountryWrapper, SystemUserWrapper } from 'model/support-types';
import { LookupsService } from 'services/lookups-service';
import { requireServices } from 'support/activation';
import { HOME_ROUTE } from 'support/settings';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';

@autoinject
export class AdminCountriesRolesListScreen {
  userUniqueId: string = null;
  route: RouteConfig = null;
  filterText: string = null;
  users: UserCountryRoleModel[] = [];
  loaded = false;
  newEmptySystemUserWrapper: SystemUserWrapper = { user: null, userId: null };
  newEmptyCountryWrapper: CountryWrapper = { country: null };
  countryButtons: WidgetButton[] = [{
    icon: "fa-globe-africa",
    title: "Manage country",
    onClick: (item: CountryWrapper) => {
      this.router.navigate(`#/${HOME_ROUTE}/admin/countries/${item.country.id}`);
    },
  }];
  userButtons: WidgetButton[] = [{
    icon: "fa-user-tag",
    title: "Manage Countries Roles",
    onClick: (item: SystemUserWrapper) => {
      this.filterText = item.user.name;
    },
  }];

  constructor(private lookups: LookupsService, private systemUsersApi: SystemUsersClient, private countriesApi: CountriesClient, private router: Router, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer, private businessTypesMapping: BusinessTypesMapping, private businessKeywordsMapping: BusinessKeywordsMapping, private eventHelper: EventHelper) {
    validationRenderer.allowedParents = 'bs-form-lookup';
    this.validationController.addRenderer(validationRenderer);
  }

  async canActivate() {
    await requireServices(this.lookups);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.userUniqueId = params?.userUniqueId;
    this.route = route;
  }

  bind() {
    this.initialize();
  }

  private async initialize() {
    this.bindDetail(await this.systemUsersApi.getAllCountriesUsers());
    if (this.userUniqueId != null) {
      const user = this.users.find(a => a.user.uniqueId === this.userUniqueId);
      if (user != null)
        this.filterText = user.user.fullName;
    }
    this.loaded = true;
  }

  async canDeactivate() {
    if (this.users.some(x => x.isUserEditing || x.isApproverDelegateEditing || x.isDelegateApproverEditing || x.isCountryApproveEditing || x.isCountryContactEditing) && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  @log('debug')
  private bindDetail(dtos?: ISystemUserCountryRoleInfoModel[]) {
    this.users = dtos.map(dto => this.mapToUserCountryRoleModel(dto));
  }

  @watch((x: AdminCountriesRolesListScreen) => x.filterText)
  filterMutated() {
    const filterText = this.filterText?.trim().toLowerCase();
    const visibleUsers = this.users
      .filter(x => filterText == null || x.user.fullName.toLowerCase().includes(filterText) || x.countriesApproveWrapper.some(c => c.country.name.toLowerCase().includes(filterText)) || x.countriesContactWrapper.some(c => c.country.name.toLowerCase().includes(filterText)) || x.approverDelegatesWrapper.some(d => d.user.name.toLowerCase().includes(filterText)) || x.delegateApproversWrapper.some(d => d.user.name.toLowerCase().includes(filterText)))
      .map(x => x.user.id);
    this.users.forEach(user => {
      user.isVisible = visibleUsers.includes(user.user.id);
    })
  }

  get homeRoute() {
    return HOME_ROUTE;
  }

  get title() {
    return 'Countries Roles';
  }

  @watch((x: AdminCountriesRolesListScreen) => x.users)
  private modelChanged() {
    this.route.navModel.setTitle(this.title);
    this.validationController.reset();
  }

  private getOrAddUser(dto: ISystemUserDetailDTO): UserCountryRoleModel {
    let user = this.users.find(x => x.user.id === dto.id)
    if (user == null) {
      user = this.mapToUserCountryRoleModel({ user: dto, countriesApprove: [], countriesContact: [], approverDelegates: [], delegateApprovers: [] } as ISystemUserCountryRoleInfoModel);
      this.users.push(user);
      this.users.sort((a, b) => a.user.fullName.localeCompare(b.user.fullName));
    }
    return user;
  }

  private 

  private mapToUserCountryRoleModel(dto: ISystemUserCountryRoleInfoModel) {
    return {
      user: dto.user,
      countriesApproveWrapper: dto.countriesApprove.map(x => ({ country: this.businessKeywordsMapping.mapToCountry(x) } as CountryWrapper)),
      countriesContactWrapper: dto.countriesContact.map(x => ({ country: this.businessKeywordsMapping.mapToCountry(x) } as CountryWrapper)),
      approverDelegatesWrapper: dto.approverDelegates.map(x => ({ user: this.businessTypesMapping.mapUserToUserContact(x), userId: x.id } as SystemUserWrapper)),
      delegateApproversWrapper: dto.delegateApprovers.map(x => ({ user: this.businessTypesMapping.mapUserToUserContact(x), userId: x.id } as SystemUserWrapper)),
      isVisible: true,
      isExpanded: false,
      isUserEditing: false,
      isCountryApproveEditing: false,
      isCountryContactEditing: false,
      isApproverDelegateEditing: false,
      isDelegateApproverEditing: false,
      saveUser: (item: ISystemUserDetailDTO) => {
        return this.systemUsersApi.updateSystemUser(dto.user.id, new SystemUserUpdateDTO({ countriesContactEmail: item.countriesContactEmail }))
          .then((user) => {
            this.toastHelper.toast('User has been <b>updated</b>.', 'User updated', ToastType.Success, ToastFormat.Html);
            return user;
          });
      },
      addNewCountryApprove: (item: CountryWrapper) => {
        return this.countriesApi.addCountryApprover(item.country.id, new AddCountryApproverModel({ approverUniqueId: dto.user.uniqueId }))
          .then(() => {
            const user = this.users.find(x => x.user.id === dto.user.id);
            user.countriesApproveWrapper.unshift(item);
            user.countriesApproveWrapper = user.countriesApproveWrapper.sort((a, b) => ('' + a.country.name).localeCompare(b.country.name));
            this.eventHelper.publish('settings-changed');
            this.toastHelper.toast('Country has been <b>added</b>.', 'Country added', ToastType.Success, ToastFormat.Html);
          });
      },
      removeCountryApprove: (item: CountryWrapper) => {
        return this.countriesApi.removeCountryApprover(item.country.id, new RemoveCountryApproverModel({ approverId: dto.user.id }))
          .then(() => {
            const user = this.users.find(x => x.user.id === dto.user.id);
            user.countriesApproveWrapper.splice(user.countriesApproveWrapper.indexOf(item), 1);
            this.eventHelper.publish('settings-changed');
            this.toastHelper.toast('Country has been <b>removed</b>.', 'Country removed', ToastType.Success, ToastFormat.Html);
          });
      },
      addNewCountryContact: (item: CountryWrapper) => {
        return this.countriesApi.addCountryContact(item.country.id, new AddCountryContactModel({ contactUniqueId: dto.user.uniqueId }))
          .then(() => {
            const user = this.users.find(x => x.user.id === dto.user.id);
            user.countriesContactWrapper.unshift(item);
            user.countriesContactWrapper = user.countriesContactWrapper.sort((a, b) => ('' + a.country.name).localeCompare(b.country.name));
            this.toastHelper.toast('Country has been <b>added</b>.', 'Country added', ToastType.Success, ToastFormat.Html);
          });
      },
      removeCountryContact: (item: CountryWrapper) => {
        return this.countriesApi.removeCountryContact(item.country.id, new RemoveCountryContactModel({ contactId: dto.user.id }))
          .then(() => {
            const user = this.users.find(x => x.user.id === dto.user.id);
            user.countriesContactWrapper.splice(user.countriesContactWrapper.indexOf(item), 1);
            this.toastHelper.toast('Country has been <b>removed</b>.', 'Country removed', ToastType.Success, ToastFormat.Html);
          });
      },
      addNewApproverDelegate: (item: SystemUserWrapper) => {
        return this.systemUsersApi.addApproverDelegate(dto.user.id, new AddApproverDelegateModel({ delegateUniqueId: item.user.uniqueid }))
          .then((data) => {
            item.userId = data.id;
            const approver = this.users.find(x => x.user.id === dto.user.id);
            approver.approverDelegatesWrapper.unshift(item);
            approver.approverDelegatesWrapper = approver.approverDelegatesWrapper.sort((a, b) => ('' + a.user.name).localeCompare(b.user.name));
            const delegate = this.getOrAddUser(data);
            delegate.delegateApproversWrapper.unshift({ user: this.businessTypesMapping.mapUserToUserContact(approver.user), userId: approver.user.id } as SystemUserWrapper)
            delegate.delegateApproversWrapper = delegate.delegateApproversWrapper.sort((a, b) => ('' + a.user.name).localeCompare(b.user.name));
            this.toastHelper.toast('Delegate has been <b>added</b>.', 'Delegate added', ToastType.Success, ToastFormat.Html);
          });
      },
      removeApproverDelegate: (item: SystemUserWrapper) => {
        return this.systemUsersApi.removeApproverDelegate(dto.user.id, new RemoveApproverDelegateModel({ delegateId: item.userId }))
          .then(() => {
            const approver = this.users.find(x => x.user.id === dto.user.id);
            approver.approverDelegatesWrapper.splice(approver.approverDelegatesWrapper.indexOf(item), 1);
            const delegate = this.users.find(x => x.user.id === item.userId);
            delegate.delegateApproversWrapper.splice(delegate.delegateApproversWrapper.findIndex(x => x.userId === approver.user.id), 1);
            this.toastHelper.toast('Delegate has been <b>removed</b>.', 'Delegate removed', ToastType.Success, ToastFormat.Html);
          });
      },
      addNewDelegateApprover: (item: SystemUserWrapper) => {
        return this.systemUsersApi.addDelegateApprover(dto.user.id, new AddDelegateApproverModel({ approverUniqueId: item.user.uniqueid }))
          .then((data) => {
            item.userId = data.id;
            const delegate = this.users.find(x => x.user.id === dto.user.id);
            delegate.delegateApproversWrapper.unshift(item);
            delegate.delegateApproversWrapper = delegate.delegateApproversWrapper.sort((a, b) => ('' + a.user.name).localeCompare(b.user.name));
            const approver = this.getOrAddUser(data);
            approver.approverDelegatesWrapper.unshift({ user: this.businessTypesMapping.mapUserToUserContact(delegate.user), userId: delegate.user.id } as SystemUserWrapper)
            approver.approverDelegatesWrapper = approver.approverDelegatesWrapper.sort((a, b) => ('' + a.user.name).localeCompare(b.user.name));
            this.toastHelper.toast('Delegate\'s approver has been <b>added</b>.', 'Delegate\'s approver added', ToastType.Success, ToastFormat.Html);
          });
      },
      removeDelegateApprover: (item: SystemUserWrapper) => {
        return this.systemUsersApi.removeDelegateApprover(dto.user.id, new RemoveDelegateApproverModel({ approverId: item.userId }))
          .then(() => {
            const delegate = this.users.find(x => x.user.id === dto.user.id);
            delegate.delegateApproversWrapper.splice(delegate.delegateApproversWrapper.indexOf(item), 1);
            const approver = this.users.find(x => x.user.id === item.userId);
            approver.approverDelegatesWrapper.splice(approver.approverDelegatesWrapper.findIndex(x => x.userId === delegate.user.id), 1);
            this.toastHelper.toast('Delegate\'s approver has been <b>removed</b>.', 'Delegate\'s approver removed', ToastType.Success, ToastFormat.Html);
          });
      }
    } as UserCountryRoleModel;
  }
}
